import { useEffect, useRef, useState, type ReactNode } from "react";
import type { EventOption, InteractionEvent } from "../../types";
import { nextQuickActions } from "../../utils/eventOptionSelection";
import { calcTotalWaitTime, formatDuration, formatElapsed, formatEventType, formatTime } from "../../utils/tripDisplay";
import { pickStepButtons, UNDO_MS } from "../../hooks/useStepLogger";
import "../home/LiveNowCard.css";

export type ActivityStatus = "ready" | "live" | "wrapped";

interface ActivityHeroProps {
  name: ReactNode;
  icon: ReactNode;
  /** Short facts under the name: where it sits, its type, the day. */
  meta: ReactNode[];
  status: ActivityStatus;
  /** The steps logged so far, any order. */
  events: InteractionEvent[];
  /** The steps this activity offers, in button order. */
  options: EventOption[];
  zone?: string;
  postedWait?: number | null;
  /** Logs a step now and hands back the event, so it can be undone. */
  onLog: (opt: EventOption) => InteractionEvent;
  onUndo: (event: InteractionEvent) => void;
  /** The ⋮ actions menu, top right. */
  menu?: ReactNode;
  /** Anything to add under the title, e.g. "No place linked — add one". */
  extra?: ReactNode;
  /** The numbers strip at the bottom: posted wait, showtime, delays. */
  stats?: ReactNode;
  /** The "Edit buttons" control. */
  editButtons?: ReactNode;
}

/** Re-renders every second while `on`, for the running timer. */
function useNow(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [on]);
  return now;
}

function stepLabel(event: InteractionEvent, options: EventOption[]): string {
  if (event.customLabel) return event.customLabel;
  return options.find((o) => o.eventType === event.eventType)?.displayName ?? formatEventType(event.eventType);
}

/** At most two rows of four step tiles. */
const MAX_TILES = 8;

/** How many rounds a meal had, and how long the orders took to come, on average. */
function roundSummary(events: InteractionEvent[]): { count: number; avgWaitMs: number | null } | null {
  const byRound = new Map<number, { placed?: number; received?: number }>();
  for (const e of events) {
    if (e.roundIndex == null) continue;
    const r = byRound.get(e.roundIndex) ?? {};
    if (e.eventType === "ORDER_PLACED") r.placed = e.eventTimestamp.toMillis();
    if (e.eventType === "ORDER_RECEIVED") r.received = e.eventTimestamp.toMillis();
    byRound.set(e.roundIndex, r);
  }
  if (byRound.size === 0) return null;
  const waits = [...byRound.values()]
    .filter((r) => r.placed != null && r.received != null && r.received >= r.placed)
    .map((r) => r.received! - r.placed!);
  const avgWaitMs = waits.length > 0 ? waits.reduce((a, b) => a + b, 0) / waits.length : null;
  return { count: byRound.size, avgWaitMs };
}

function shortDuration(ms: number): string {
  return ms < 60_000 ? "<1m" : formatDuration(ms / 1000);
}

/**
 * The top of an activity's page, in the same look as the live card on Today:
 * a big clock (running while it's live, the total once it's wrapped), every
 * step as a bar with how long it lasted, and the next step as one big button
 * that logs straight away and offers Undo.
 */
export function ActivityHero({
  name,
  icon,
  meta,
  status,
  events: unsorted,
  options,
  zone,
  postedWait,
  onLog,
  onUndo,
  menu,
  extra,
  stats,
  editButtons,
}: ActivityHeroProps) {
  const live = status === "live";
  const now = useNow(live);
  const events = [...unsorted].sort((a, b) => a.eventTimestamp.toMillis() - b.eventTimestamp.toMillis());

  const [undo, setUndo] = useState<{ event: InteractionEvent; label: string } | null>(null);
  const undoTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (undoTimer.current) window.clearTimeout(undoTimer.current);
  }, []);
  const [busy, setBusy] = useState(false);

  const log = (opt: EventOption) => {
    if (busy) return;
    setBusy(true);
    const event = onLog(opt);
    setUndo({ event, label: opt.displayName });
    if (undoTimer.current) window.clearTimeout(undoTimer.current);
    undoTimer.current = window.setTimeout(() => setUndo(null), UNDO_MS);
    // A short guard against a double tap logging the step twice.
    window.setTimeout(() => setBusy(false), 400);
  };
  const undoLast = () => {
    if (!undo) return;
    onUndo(undo.event);
    setUndo(null);
  };

  const quick = nextQuickActions(options, events);
  const { primary, secondary } = pickStepButtons(quick.options, quick.branching);

  const first = events[0] ?? null;
  const last = events[events.length - 1] ?? null;
  const startMs = first?.eventTimestamp.toMillis() ?? 0;
  const endMs = live ? now : last?.eventTimestamp.toMillis() ?? 0;
  const timeOf = (e: InteractionEvent) => formatTime(e.eventTimestamp.toDate(), e.timezone || zone);

  // Every step with how long it lasted, up to the next one. While it's live
  // the latest is still running; once it's wrapped the last step is where it
  // ended, so it has no length of its own.
  // A meal's rounds have their own section below, so each round's Placed and
  // Received stay off the tiles; the card says how many there were instead.
  const tileEvents = events.filter((e) => e.roundIndex == null);
  const allSteps = tileEvents.map((e, i) => {
    const next = tileEvents[i + 1] ?? null;
    const start = e.eventTimestamp.toMillis();
    if (next) return { event: e, kind: "done" as const, label: shortDuration(next.eventTimestamp.toMillis() - start) };
    if (live) return { event: e, kind: "current" as const, label: formatElapsed(now - start) };
    return { event: e, kind: "end" as const, label: timeOf(e) };
  });
  // A long day (an airport, a park) keeps to two rows: the latest steps, and
  // a count of the earlier ones, which are all in Steps below.
  const hiddenSteps = allSteps.length > MAX_TILES ? allSteps.length - (MAX_TILES - 1) : 0;
  const steps = hiddenSteps > 0 ? allSteps.slice(hiddenSteps) : allSteps;

  const rounds = roundSummary(events);

  // How long you queued, next to what the sign said.
  const waited = calcTotalWaitTime(events);

  const statusLabel = live ? "Live" : status === "wrapped" ? "Wrapped" : "Ready";
  const showNext = primary && status !== "wrapped";
  const metaItems = meta.filter(Boolean);

  return (
    <section className="live-now activity-hero" aria-label="Activity">
      <div className={`ln-card ah-${status}`}>
        <div className="ln-head">
          <span className="ln-icon">{icon}</span>
          <span className={`ln-live${live ? "" : " ah-pill"}`}>{statusLabel}</span>
          {menu}
        </div>
        {/* The name gets the card's full width, under the icon row. */}
        <h1 className="ln-name ah-name">{name}</h1>
        {metaItems.length > 0 && (
          <div className="ln-meta ah-meta">
            {metaItems.map((m, i) => (
              <span key={i}>{m}</span>
            ))}
          </div>
        )}
        {extra}

        {first && (
          <div className="ln-timer">
            <span className="ln-clock">
              {live
                ? formatElapsed(endMs - startMs)
                : events.length > 1
                  ? formatDuration((endMs - startMs) / 1000)
                  : timeOf(first)}
            </span>
            <span className="ln-since">
              {live
                ? `since ${stepLabel(first, options)}`
                : events.length > 1
                  ? `${timeOf(first)} – ${timeOf(last!)}`
                  : stepLabel(first, options)}
            </span>
          </div>
        )}

        {(waited > 0 || rounds) && (
          <div className="ah-chips">
            {waited > 0 && (
              <div className="ln-waited">
                Waited <b>{formatDuration(waited)}</b>
                {postedWait ? ` · posted ${postedWait}m` : ""}
              </div>
            )}
            {rounds && (
              <div className="ln-waited">
                <b>{rounds.count}</b> {rounds.count === 1 ? "round" : "rounds"}
                {rounds.avgWaitMs != null ? ` · ${shortDuration(rounds.avgWaitMs)} avg to arrive` : ""}
              </div>
            )}
          </div>
        )}

        {(steps.length > 0 || hiddenSteps > 0 || showNext) && (
          <ol className="ln-steps ah-steps" aria-label="Steps">
            {hiddenSteps > 0 && (
              <li className="done earlier">
                <span className="bar" />
                <span className="lbl">Earlier</span>
                <span className="dur">+{hiddenSteps}</span>
                <span className="at">steps</span>
              </li>
            )}
            {steps.map(({ event: e, kind, label }, i) => (
              <li key={`${e.eventTimestamp.toMillis()}-${i}`} className={`done ${kind}`}>
                <span className="bar" />
                <span className="lbl">{stepLabel(e, options)}</span>
                <span className="dur">{label}</span>
                {kind !== "end" && <span className="at">{timeOf(e)}</span>}
              </li>
            ))}
            {showNext && (
              <li className="next">
                <span className="bar" />
                <span className="lbl">{primary.displayName}</span>
                <span className="dur">next</span>
              </li>
            )}
          </ol>
        )}

        {(primary || secondary.length > 0) && (
          <div className="ln-actions">
            {primary && (
              <button
                type="button"
                className={status === "wrapped" ? "ln-secondary" : "ln-primary"}
                onClick={() => log(primary)}
                disabled={busy}
              >
                {primary.displayName}
              </button>
            )}
            {secondary.map((opt) => (
              <button key={opt.id} type="button" className="ln-secondary" onClick={() => log(opt)} disabled={busy}>
                {opt.displayName}
              </button>
            ))}
          </div>
        )}

        {undo ? (
          <div className="ln-undo" role="status">
            <span>Logged {undo.label}</span>
            <button type="button" onClick={undoLast}>Undo</button>
          </div>
        ) : (
          // Empty (and hidden) when this person can't change the buttons.
          <div className="ln-foot ah-foot">{editButtons}</div>
        )}

        {stats}
      </div>
    </section>
  );
}
